/**
 * Fennie Launch Lock - Curated stage artwork registry.
 *
 * Single source of truth for the companion's visual identity.
 * 6 stages x 4 life states = 24 curated PNGs hosted on the Lovable CDN.
 *
 * Always resolves to a real image - no procedural generation, no shield
 * fallback. Legacy stage/state aliases are normalised here so older
 * pet_status / discipline_records rows never crash the UI.
 */

import i18n from '@/lib/i18n';
import eggHealthy from '@/assets/fennie/fennie-egg-healthy.png.asset.json';
import eggDeclining from '@/assets/fennie/fennie-egg-declining.png.asset.json';
import eggCritical from '@/assets/fennie/fennie-egg-critical.png.asset.json';
import eggPassed from '@/assets/fennie/fennie-egg-passed.png.asset.json';

import babyHealthy from '@/assets/fennie/fennie-baby-healthy.png.asset.json';
import babyDeclining from '@/assets/fennie/fennie-baby-declining.png.asset.json';
import babyCritical from '@/assets/fennie/fennie-baby-critical.png.asset.json';
import babyPassed from '@/assets/fennie/fennie-baby-passed.png.asset.json';

import commonHealthy from '@/assets/fennie/fennie-common-healthy.png.asset.json';
import commonDeclining from '@/assets/fennie/fennie-common-declining.png.asset.json';
import commonCritical from '@/assets/fennie/fennie-common-critical.png.asset.json';
import commonPassed from '@/assets/fennie/fennie-common-passed.png.asset.json';

import rareHealthy from '@/assets/fennie/fennie-rare-healthy.png.asset.json';
import rareDeclining from '@/assets/fennie/fennie-rare-declining.png.asset.json';
import rareCritical from '@/assets/fennie/fennie-rare-critical.png.asset.json';
import rarePassed from '@/assets/fennie/fennie-rare-passed.png.asset.json';

import epicHealthy from '@/assets/fennie/fennie-epic-healthy.png.asset.json';
import epicDeclining from '@/assets/fennie/fennie-epic-declining.png.asset.json';
import epicCritical from '@/assets/fennie/fennie-epic-critical.png.asset.json';
import epicPassed from '@/assets/fennie/fennie-epic-passed.png.asset.json';

import legendaryHealthy from '@/assets/fennie/fennie-legendary-healthy.png.asset.json';
import legendaryDeclining from '@/assets/fennie/fennie-legendary-declining.png.asset.json';
import legendaryCritical from '@/assets/fennie/fennie-legendary-critical.png.asset.json';
import legendaryPassed from '@/assets/fennie/fennie-legendary-passed.png.asset.json';

export type FennieStage = 'egg' | 'baby' | 'common' | 'rare' | 'epic' | 'legendary';
export type FennieLifeState = 'healthy' | 'declining' | 'critical' | 'passed';

const STAGE_ALIASES: Record<string, FennieStage> = {
  egg: 'egg',
  baby: 'baby',
  common: 'common',
  teen: 'common',
  rare: 'rare',
  epic: 'epic',
  adult: 'epic',
  legendary: 'legendary',
  master: 'legendary',
};

const STATE_ALIASES: Record<string, FennieLifeState> = {
  healthy: 'healthy',
  declining: 'declining',
  critical: 'critical',
  passed: 'passed',
  dead: 'passed',
};

export const FENNIE_STAGE_ASSETS: Record<
  FennieStage,
  Record<FennieLifeState, string>
> = {
  egg: {
    healthy: eggHealthy.url,
    declining: eggDeclining.url,
    critical: eggCritical.url,
    passed: eggPassed.url,
  },
  baby: {
    healthy: babyHealthy.url,
    declining: babyDeclining.url,
    critical: babyCritical.url,
    passed: babyPassed.url,
  },
  common: {
    healthy: commonHealthy.url,
    declining: commonDeclining.url,
    critical: commonCritical.url,
    passed: commonPassed.url,
  },
  rare: {
    healthy: rareHealthy.url,
    declining: rareDeclining.url,
    critical: rareCritical.url,
    passed: rarePassed.url,
  },
  epic: {
    healthy: epicHealthy.url,
    declining: epicDeclining.url,
    critical: epicCritical.url,
    passed: epicPassed.url,
  },
  legendary: {
    healthy: legendaryHealthy.url,
    declining: legendaryDeclining.url,
    critical: legendaryCritical.url,
    passed: legendaryPassed.url,
  },
};

export function normaliseStage(stage: string | null | undefined): FennieStage {
  if (!stage) return 'egg';
  return STAGE_ALIASES[stage.toLowerCase()] ?? 'egg';
}

export function normaliseLifeState(
  state: string | null | undefined,
): FennieLifeState {
  if (!state) return 'healthy';
  return STATE_ALIASES[state.toLowerCase()] ?? 'healthy';
}

/**
 * Always returns a real, importable URL - never null.
 * Use this everywhere the companion is shown.
 */
export function getFennieArtwork(
  stage: string | null | undefined,
  lifeState: string | null | undefined,
): string {
  const s = normaliseStage(stage);
  const l = normaliseLifeState(lifeState);
  return FENNIE_STAGE_ASSETS[s][l];
}

const preloadedStages = new Set<FennieStage>();

/**
 * Warms the browser cache for every life state of a stage so the artwork
 * swaps instantly when the pet evolves or its life state changes.
 * The asset URLs are content-addressed and immutable, so this is a
 * one-time fetch per stage and never serves stale artwork.
 */
export function preloadStageArtwork(stage: string | null | undefined): void {
  if (typeof window === 'undefined') return;
  const s = normaliseStage(stage);
  if (preloadedStages.has(s)) return;
  preloadedStages.add(s);
  for (const url of Object.values(FENNIE_STAGE_ASSETS[s])) {
    const img = new Image();
    img.decoding = 'async';
    img.src = url;
  }
}

/** Single source for the stage label shown in UI ("Common", "Legendary"…). */
export function formatStageLabel(stage: string | null | undefined): string {
  return i18n.t(`fennie:stage.${normaliseStage(stage)}`);
}

export function formatLifeStateLabel(
  state: string | null | undefined,
): string {
  return i18n.t(`fennie:lifeState.${normaliseLifeState(state)}`);
}
